import styled from '@emotion/styled'
import * as Popover from '@radix-ui/react-popover'
import { partition } from 'lodash'
import { ReactNode } from 'react'

import { ResourceAvailabilitySource, ShiftHours } from '@bemlo/enums'
import { isTruthy } from '@bemlo/utils'

import { Color, theme } from '../../../../constants'
import { Flex, Icon, IconName, IconSize } from '../../../../elements'
import { useTranslations } from '../../../../translate'
import { composableZIndex } from '../consts'
import { UIAvailability } from '../types'
import { useSchedule } from '../useSchedule'
import { PopoverContentWrapper } from './Shift'
import { useAvailabilityStyle } from '../utils/availability-style'

type CellProps = {
  isTitle?: boolean
  rightShadow?: boolean
  disabled?: boolean
  weekend?: boolean
  error?: boolean
  relative?: boolean
  backgroundColor?: string
}

const cellBackground = (props: CellProps) => {
  if (props.disabled) return Color.MEDIUM_GREY
  if (props.weekend) return Color.BACKGROUND_GREY
  if (props.error)
    return `repeating-linear-gradient(
    -45deg, /* Angle of the gradient */
    ${Color.LIGHT_RED}, /* First color */
    ${Color.LIGHT_RED} 6px, /* Start of the second color */
    ${Color.WHITE} 6px, /* Second color */
    ${Color.WHITE} 12px /* End of the second color */
  );`
}

export const Cell = styled.td<CellProps>((props) => ({
  position: props.isTitle ? 'sticky' : 'relative',
  left: props.isTitle ? 0 : undefined,
  zIndex: props.isTitle ? composableZIndex.cellTitle : undefined,
  background: props.isTitle ? Color.WHITE : cellBackground(props),
  borderLeft: props.isTitle ? undefined : `1px solid ${Color.MEDIUM_GREY}`,
  borderTop: `1px solid ${Color.MEDIUM_GREY}`,
  color: props.isTitle ? Color.DARK_GREY : undefined,
  fontWeight: props.isTitle ? theme.font.weight.bold : undefined,
  userSelect: props.isTitle ? 'none' : undefined,
  boxShadow: props.rightShadow ? theme.boxShadow.semi : undefined,
  backgroundColor: props.backgroundColor,
  ...(props.relative && { position: 'relative' }),
}))

const ContentContainer = styled.div<{
  isCompact: boolean
  border?: string
  backgroundColor?: string
  hasPopover: boolean
}>((props) => ({
  width: '100%',
  display: 'flex',
  textAlign: 'left',
  justifyContent: props.isCompact ? 'center' : 'space-between',
  ...(props.isCompact
    ? { height: '100%' }
    : { padding: `${theme.spacing[1.5]}px ${theme.spacing[2]}px` }),
  alignItems: 'center',
  border: 'none',
  ...(props.border && {
    border: props.border,
    borderRadius: theme.border.radius.normal,
    overflow: 'hidden',
  }),
  backgroundColor: props.backgroundColor,

  span: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },

  ':not(:last-child)': {
    marginBottom: theme.spacing[1],
  },

  ...(props.hasPopover && { cursor: 'pointer' }),
}))

type ContentProps = {
  availability: UIAvailability[]
  icon: IconName
  color: string
  title: string
  subtitle: string
  solid?: boolean
  card?: {
    backgroundColor: string
    border: string
  }
  clickable: boolean
}

const Header = styled.span({
  fontWeight: theme.font.weight.medium,
  fontSize: 12,
})

const Subtitle = styled.span({
  fontSize: 10,
  opacity: 0.75,
})

const Content = (props: ContentProps) => {
  const { columnWidth, availabilityPopover: PopoverContent } = useSchedule()

  const isCompact = columnWidth < 100
  const iconSize: IconSize = 'sm'
  const hasPopover =
    !!props.clickable && !!PopoverContent && !!props.availability.length

  const content = (
    <ContentContainer
      as={PopoverContent ? 'button' : 'div'}
      isCompact={isCompact}
      border={props.card?.border}
      backgroundColor={props.card?.backgroundColor}
      hasPopover={hasPopover}
    >
      {!isCompact && (
        <Flex col styles={{ color: props.color, gap: '1px' }}>
          <Header>{props.title}</Header>
          <Subtitle>{props.subtitle}</Subtitle>
        </Flex>
      )}
      <Icon
        name={props.icon}
        color={props.color}
        size={iconSize}
        design={props.solid ? 'solid' : 'outline'}
      />
    </ContentContainer>
  )

  if (!hasPopover) {
    return content
  }

  const candidateId = props.availability[0].candidateId

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{content}</Popover.Trigger>
      <PopoverContentWrapper
        sideOffset={4}
        align="start"
        side="right"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <PopoverContent
          candidate={{ id: candidateId }}
          availability={props.availability}
        />
      </PopoverContentWrapper>
    </Popover.Root>
  )
}

type AvailabilityCellProps = {
  disabled?: boolean
  weekend?: boolean
  children?: ReactNode
  isOccupied: boolean
  isRequired?: boolean
  availability: UIAvailability[]
  showMatch?: ShiftHours
}

export const AvailabilityCell = (props: AvailabilityCellProps) => {
  const t = useTranslations()
  const style = useAvailabilityStyle(props.availability)

  const noAvailabilityContent = (
    <Cell disabled={props.disabled} weekend={props.weekend}>
      {props.children}
    </Cell>
  )

  if (!props.availability.length || props.disabled) {
    if (props.isRequired) {
      return (
        <Cell disabled={props.disabled} weekend={props.weekend}>
          <Content
            availability={[]}
            icon="CheckIcon"
            color={Color.GREEN}
            title={t.shifts.required_day()}
            card={{
              backgroundColor: Color.WHITE,
              border: `1px solid ${Color.LIGHT_GREEN}`,
            }}
            subtitle=""
            clickable={false}
          />
          {props.children}
        </Cell>
      )
    }

    return noAvailabilityContent
  }

  const [available, notAvailability] = partition(
    props.availability,
    'isAvailable',
  )

  const isAvailable = available.length > 0

  const isOnVacation = notAvailability.find(
    (availability) =>
      availability.source === ResourceAvailabilitySource.VACATION,
  )
  const isBlocked = notAvailability.find(
    (availability) =>
      availability.source === ResourceAvailabilitySource.SCHEDULE,
  )

  const availableShiftHours = available
    .map(({ shiftHours }) => shiftHours)
    .filter(isTruthy)
    .map((shiftHours) => t.$shiftHours(shiftHours))

  const availableTimes = available
    .map(({ startTime, endTime }) => `${startTime} - ${endTime}`)
    .join(', ')

  const unavailableShiftHours = notAvailability
    .map(({ shiftHours }) => shiftHours)
    .filter(isTruthy)
    .map((shiftHours) => t.$shiftHours(shiftHours))

  if (isOnVacation || isBlocked) {
    return (
      <Cell
        backgroundColor={props.isOccupied ? Color.RED : style.backgroundColor}
        disabled={props.disabled}
      >
        <Content
          availability={[isOnVacation, isBlocked].filter(isTruthy)}
          icon={style.icon}
          color={props.isOccupied ? Color.LIGHT_GREY : style.color}
          title={style.title}
          subtitle={style.subtitle ?? ''}
          clickable
        />
        {props.children}
      </Cell>
    )
  }

  if (props.isOccupied) {
    return noAvailabilityContent
  }

  const availableSubtitle = availableShiftHours.join(', ') || availableTimes
  const unavailableSubtitle = unavailableShiftHours.join(', ')

  return (
    <Cell backgroundColor={style.backgroundColor} disabled={props.disabled}>
      <Content
        availability={isAvailable ? available : notAvailability}
        card={{
          backgroundColor: style.backgroundColor,
          border: `1px solid ${isAvailable ? Color.LIGHT_GREEN : Color.MEDIUM_GREY}`,
        }}
        icon={style.icon}
        color={style.color}
        title={style.title}
        subtitle={isAvailable ? availableSubtitle : unavailableSubtitle}
        clickable={isAvailable}
      />
      {props.children}
    </Cell>
  )
}
